import { translate } from "../../i18n/translate";
import { TranslationsService } from "../../i18n/TranslationsService";
import { GlobalSearchFacet } from "../../models/GlobalSearchFacet";

export interface SearchFilterOptions {
    filterLabel: string,
    placeholderLabel: string,
    data: any[]
}

type Callback = (filters: string[]) => void;

export class SearchFilter {
    private readonly element: HTMLElement;
    private readonly translationService: TranslationsService;
    private readonly options: SearchFilterOptions
    private readonly onFilterApplied: Callback
    private selectedFilters : string[] = []
    private filterIdPrefix: string = ''

    constructor(element : HTMLElement, translationService : TranslationsService, options : SearchFilterOptions, onFilterApplied: Callback, filterIdPrefix: string = '') {
        this.element = element;
        this.translationService = translationService;
        this.options = options;
        this.onFilterApplied = onFilterApplied;
        this.filterIdPrefix = filterIdPrefix || `filter-${Math.random().toString(36).substr(2, 9)}`;
        this.element.classList.add('horizontal-filter', 'horizontal-filter--multiple')
        this.render();
        
        // Initialize clear button visibility
        this.updateClearButtonVisibility();
    }

    private render() : void {
        // .horizontal-filter is already position: relative, so we can absolutely position the clear button
        // The clear button should appear to the right of the dropdown button
        // Keep buttons as direct children for flex layout and Bootstrap dropdown to work correctly
        this.element.innerHTML = `
            <button type="button" class="horizontal-filter__dropdown-btn dropdown-toggle" data-bs-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
                <span>${this.options.filterLabel}</span>
            </button>
            <button type="button" class="horizontal-filter__clear-btn">
                <svg class="icon" viewBox="0 0 32 32">
                    <use xmlns:xlink="http://www.w3.org/1999/xlink" xlink:href="/images/icons-sprite.svg#cross-32"></use>
                </svg>
                <span class="screen-reader-text">${translate('Hero.ClearFilter')}</span>
            </button>
            <div class="horizontal-filter__menu dropdown-menu">
                <div class="horizontal-filter__search">
                    <svg class="icon" viewBox="0 0 16 16">
                        <use xmlns:xlink="http://www.w3.org/1999/xlink" xlink:href="/images/icons-sprite.svg#search-16"></use>
                    </svg>
                    <input type="search" name="" value="" placeholder="${this.options.placeholderLabel}">
                </div>

                <div class="horizontal-filter__empty">${translate('Hero.NoResults')}</div>

                <ul class="horizontal-filter__options">
                    ${this.options?.data?.map(x => this.renderItem(x)).join('')}
                </ul>

                <div class="horizontal-filter__apply-btn">
                    <button class="btn btn-primary btn-block apply-filter">${translate('Hero.Apply')}</button>
                </div>
            </div>
        `;

        this.initializeEventListeners();
        
        // Update display and button state after render
        this.updateDisplayLabel();
        this.updateClearButtonVisibility();
    }

    private renderItem(item: any) : string {
        const hasChildren = item.children?.length
        const liClassName = hasChildren ? 'horizontal-filter__item--has-children' : ''
        const isChecked = this.selectedFilters.includes(item.key)

        return `
            <li class="horizontal-filter__item ${liClassName}">
                <div class="checkbox">
                    <input id="${this.filterIdPrefix}-${item.key}" value="${item.key}" type="checkbox" class="pwc-studio-checkbox" ${isChecked ? 'checked' : ''}>
                    <label for="${this.filterIdPrefix}-${item.key}">
                        <svg class="icon icon--check" viewBox="0 0 16 16">
                            <use xmlns:xlink="http://www.w3.org/1999/xlink" xlink:href="/images/icons-sprite.svg#check-16"></use>
                        </svg>
                        <svg class="icon icon--minus" viewBox="0 0 16 16" style="display: none;">
                            <use xmlns:xlink="http://www.w3.org/1999/xlink" xlink:href="/images/icons-sprite.svg#minus-16"></use>
                        </svg>
                        ${item.label}
                    </label>
                </div>

                ${hasChildren ? 

                    `<button class="horizontal-filter__nested-list-btn" data-item="${item.key}">
                        <span class="caret"></span>
                        <span class="sr-only">Test</span>
                    </button>
                    <ul class="horizontal-filter__nested-items">
                    ${item.children.map((x : GlobalSearchFacet) => {
                        const childIsChecked = this.selectedFilters.includes(x.key);
                        return `
                            <li class="horizontal-filter__item">
                                <div class="checkbox">
                                    <input id="${this.filterIdPrefix}-${x.key}" value="${x.key}" type="checkbox" class="pwc-studio-checkbox" ${childIsChecked ? 'checked' : ''}>
                                    <label for="${this.filterIdPrefix}-${x.key}">
                                        <svg class="icon" viewBox="0 0 16 16">
                                            <use xmlns:xlink="http://www.w3.org/1999/xlink" xlink:href="/images/icons-sprite.svg#check-16"></use>
                                        </svg>
                                        ${x.label}
                                    </label>
                                </div>
                            </li>
                        `;
                    }).join('')}
                        
                    </ul>` : ''
                }
            </li>
        `;
    }

    private initializeEventListeners(): void {
        const clearButton = this.element.querySelector('.horizontal-filter__clear-btn');
        const searchInput = this.element.querySelector('input[type="search"]');
        const checkboxes = this.element.querySelectorAll('.pwc-studio-checkbox');
        const dropdownMenu = this.element.querySelector('.horizontal-filter__menu');
        const expandChildrenButton = this.element.querySelectorAll('.horizontal-filter__nested-list-btn');
        const applyFilterButton = this.element.querySelector('.apply-filter');
        const dropdownToggle = this.element.querySelector('.horizontal-filter__dropdown-btn');
        
        // Handle checkbox changes (use change event instead of click to avoid label click issues)
        checkboxes?.forEach(checkbox => {
            checkbox.addEventListener('change', (event) => {
                event.stopPropagation();
                this.toggleSelect(event);
            });
            // Prevent label clicks from causing double-trigger
            const label = checkbox.closest('.checkbox')?.querySelector('label');
            if (label) {
                label.addEventListener('click', (event) => {
                    // Let the default behavior happen (which will trigger the change event)
                    // but prevent event bubbling
                    event.stopPropagation();
                });
            }
        });
    
        clearButton?.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            this.clearFilter();
        });
        
        searchInput?.addEventListener('input', (event) => this.searchFilter(event));
        
        // Prevent dropdown from closing when clicking inside
        dropdownMenu?.addEventListener('click', (event) => {
            // Only stop propagation if not clicking the apply button
            if (!(event.target as HTMLElement).closest('.apply-filter')) {
                event.stopPropagation();
            }
        });

        expandChildrenButton?.forEach(x => x.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            this.toggleNestedList(event);
        }));       
        
        applyFilterButton?.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            this.applyFilter();
            
            // Close the dropdown after applying
            if (dropdownToggle) {
                const bootstrap = (window as any).bootstrap;
                if (bootstrap && bootstrap.Dropdown) {
                    const dropdown = bootstrap.Dropdown.getInstance(dropdownToggle);
                    if (dropdown) {
                        dropdown.hide();
                    }
                }
                // Fallback: manually close
                dropdownToggle.setAttribute('aria-expanded', 'false');
                if (dropdownMenu) {
                    dropdownMenu.classList.remove('show');
                }
            }
        });

        // Handle dropdown show/hide events - add/remove "open" class like legacy solution
        if (dropdownToggle) {
            dropdownToggle.addEventListener('show.bs.dropdown', () => {
                // Add "open" class to parent div when dropdown opens
                this.element.classList.add('open');
            });

            dropdownToggle.addEventListener('shown.bs.dropdown', () => {
                // Ensure "open" class is still present after dropdown is fully shown
                this.element.classList.add('open');
            });

            dropdownToggle.addEventListener('hide.bs.dropdown', () => {
                // Remove "open" class when dropdown starts closing
                this.element.classList.remove('open');
            });

            dropdownToggle.addEventListener('hidden.bs.dropdown', () => {
                // Ensure "open" class is removed after dropdown is fully closed
                this.element.classList.remove('open');
            });
        }

      }
    
      public clearFilter() {
        // Clear the selected filters array
        this.selectedFilters = [];
        
        // Uncheck all checkboxes in the filter
        const checkboxes = this.element.querySelectorAll('.pwc-studio-checkbox') as NodeListOf<HTMLInputElement>;
        checkboxes.forEach(checkbox => {
            if (checkbox.checked) {
                checkbox.checked = false;
                checkbox.removeAttribute('checked');
            }
            // Clear indeterminate state
            checkbox.indeterminate = false;
        });

        // Remove indeterminate classes from all items and reset icons
        const parentItems = this.element.querySelectorAll('.horizontal-filter__item--has-children');
        parentItems.forEach(item => {
            item.classList.remove('horizontal-filter__item--indeterminate');
            this.updateParentIcon(item, 'normal');
        });
        
        // Clear search input
        const searchInput = this.element.querySelector('input[type="search"]') as HTMLInputElement;
        if (searchInput) {
            searchInput.value = '';
            // Trigger search to show all items again
            const mockEvent = { target: searchInput } as unknown as Event;
            this.searchFilter(mockEvent);
        }
        
        // Collapse all expanded nested lists
        const expandedItems = this.element.querySelectorAll('.horizontal-filter__item--open');
        expandedItems.forEach(item => {
            item.classList.remove('horizontal-filter__item--open');
        });
        
        // Apply the cleared filters
        this.onFilterApplied(this.selectedFilters);
        
        // Update display after clearing
        this.updateDisplayLabel();
        this.updateClearButtonVisibility();
      }

      private applyFilter() {
        // Ensure we have the latest selected filters from checkboxes
        this.syncSelectedFiltersFromCheckboxes();
        
        // Call the callback with selected filters (create a copy to avoid reference issues)
        // Filter out any empty or invalid values and ensure all are strings
        const filtersToApply = this.selectedFilters
            .filter(f => f && String(f).trim() !== '')
            .map(f => String(f));
        
        this.onFilterApplied(filtersToApply);
        
        // Update display after applying
        this.updateDisplayLabel();
        this.updateClearButtonVisibility();
      }
      
      private syncSelectedFiltersFromCheckboxes() {
        // Sync selectedFilters array with actual checkbox states
        const checkboxes = this.element.querySelectorAll('.pwc-studio-checkbox') as NodeListOf<HTMLInputElement>;
        this.selectedFilters = [];
        checkboxes.forEach(checkbox => {
            if (checkbox.checked && checkbox.value) {
                // Ensure value is a string
                const value = String(checkbox.value);
                if (!this.selectedFilters.includes(value)) {
                    this.selectedFilters.push(value);
                }
            }
        });
      }
      
      private searchFilter(event: Event) {
        const input = event.target as HTMLInputElement;
        const term = input.value.toLowerCase();
        const filter = this.element;
        const listItems = filter.querySelectorAll('.horizontal-filter__options li');
        const emptyMessage = filter.querySelector('.horizontal-filter__empty');
    
        let hasVisibleItem = false;
    
        listItems.forEach(item => {
            const label = item.querySelector('label') as HTMLElement;
            const text = label.textContent || label.innerText;
            const isVisible = text.toLowerCase().indexOf(term) > -1;
            
            if (!hasVisibleItem && isVisible) {
                hasVisibleItem = true;
            }
        });
    
    }

      private toggleSelect(event: Event) {
        const element = event.target as HTMLInputElement;
        if (!element || element.type !== 'checkbox') return;
        
        const value = element.value;
        if (!value) return;
        
        if (element.checked) {
            this.addToSelected(value);
            element.setAttribute('checked', 'checked');
            
            // If this is a parent, select all children
            this.selectAllChildren(element);
            
            // If this is a child, check if parent should be selected (all children selected)
            this.checkParentSelection(element);
        } else {
            this.removeFromSelected(value);
            element.removeAttribute('checked');
            
            // If this is a parent, deselect all children
            this.deselectAllChildren(element);
            
            // If this is a child, check if parent should be deselected
            this.checkParentDeselection(element);
        }
        
        // Update display label and clear button visibility
        this.updateDisplayLabel();
        this.updateClearButtonVisibility();
      }

      private toggleNestedList(event: Event) {
        const element = event.target as HTMLElement;
        const parentElement = this.findAncestorOfType(element, 'li');
        parentElement?.classList.toggle('horizontal-filter__item--open');

      }

      private findAncestorOfType(element : HTMLElement, type: string) {
        type = type.toUpperCase();
        let currentElement = element;

        while (currentElement) {
            if (currentElement.nodeName === type) {
                return currentElement;
            }
            currentElement = currentElement.parentNode as HTMLElement;
        }
        return null;
    }

    private addToSelected(value: string) {
        if (!this.selectedFilters.includes(value)) {
            this.selectedFilters.push(value);
        }
    }

    private removeFromSelected(value: string) {
        const index = this.selectedFilters.indexOf(value);
        if (index > -1) {
            this.selectedFilters.splice(index, 1);
        }
    }

    private selectAllChildren(parentCheckbox: HTMLInputElement) {
        const parentLi = this.findAncestorOfType(parentCheckbox, 'li');
        if (!parentLi) return;

        const childCheckboxes = parentLi.querySelectorAll('.horizontal-filter__nested-items .pwc-studio-checkbox') as NodeListOf<HTMLInputElement>;
        
        childCheckboxes.forEach(childCheckbox => {
            if (!childCheckbox.checked) {
                childCheckbox.checked = true;
                childCheckbox.setAttribute('checked', 'checked');
                this.addToSelected(childCheckbox.value);
            }
        });

        // Remove indeterminate state from parent
        parentCheckbox.indeterminate = false;
        parentLi.classList.remove('horizontal-filter__item--indeterminate');
        this.updateParentIcon(parentLi, 'checked');
    }

    private deselectAllChildren(parentCheckbox: HTMLInputElement) {
        const parentLi = this.findAncestorOfType(parentCheckbox, 'li');
        if (!parentLi) return;

        const childCheckboxes = parentLi.querySelectorAll('.horizontal-filter__nested-items .pwc-studio-checkbox') as NodeListOf<HTMLInputElement>;
        
        childCheckboxes.forEach(childCheckbox => {
            if (childCheckbox.checked) {
                childCheckbox.checked = false;
                childCheckbox.removeAttribute('checked');
                this.removeFromSelected(childCheckbox.value);
            }
        });

        // Remove indeterminate state from parent
        parentCheckbox.indeterminate = false;
        parentLi.classList.remove('horizontal-filter__item--indeterminate');
        this.updateParentIcon(parentLi, 'normal');
    }

    private checkParentDeselection(childCheckbox: HTMLInputElement) {
        const childLi = this.findAncestorOfType(childCheckbox, 'li');
        if (!childLi) return;

        const parentLi = childLi.parentElement?.closest('.horizontal-filter__item--has-children');
        if (!parentLi) return;

        const parentCheckbox = parentLi.querySelector('.pwc-studio-checkbox') as HTMLInputElement;
        if (!parentCheckbox) return;

        // Check children state
        const siblingCheckboxes = parentLi.querySelectorAll('.horizontal-filter__nested-items .pwc-studio-checkbox') as NodeListOf<HTMLInputElement>;
        const selectedChildren = Array.from(siblingCheckboxes).filter(cb => cb.checked);
        const totalChildren = siblingCheckboxes.length;

        if (selectedChildren.length === 0) {
            // No children selected - uncheck parent and remove indeterminate
            parentCheckbox.checked = false;
            parentCheckbox.indeterminate = false;
            parentCheckbox.removeAttribute('checked');
            parentLi.classList.remove('horizontal-filter__item--indeterminate');
            this.updateParentIcon(parentLi, 'normal');
            this.removeFromSelected(parentCheckbox.value);
        } else if (selectedChildren.length < totalChildren) {
            // Some children selected - set parent to indeterminate
            parentCheckbox.checked = false;
            parentCheckbox.indeterminate = true;
            parentCheckbox.removeAttribute('checked');
            parentLi.classList.add('horizontal-filter__item--indeterminate');
            this.updateParentIcon(parentLi, 'indeterminate');
            this.removeFromSelected(parentCheckbox.value);
        }
        // If all children are selected, parent should remain checked (handled by checkParentSelection)
    }

    private checkParentSelection(childCheckbox: HTMLInputElement) {
        const childLi = this.findAncestorOfType(childCheckbox, 'li');
        if (!childLi) return;

        const parentLi = childLi.parentElement?.closest('.horizontal-filter__item--has-children');
        if (!parentLi) return;

        const parentCheckbox = parentLi.querySelector('.pwc-studio-checkbox') as HTMLInputElement;
        if (!parentCheckbox) return;

        // Check children state
        const siblingCheckboxes = parentLi.querySelectorAll('.horizontal-filter__nested-items .pwc-studio-checkbox') as NodeListOf<HTMLInputElement>;
        const selectedChildren = Array.from(siblingCheckboxes).filter(cb => cb.checked);
        const totalChildren = siblingCheckboxes.length;

        if (selectedChildren.length === totalChildren) {
            // All children selected - check parent and remove indeterminate
            parentCheckbox.checked = true;
            parentCheckbox.indeterminate = false;
            parentCheckbox.setAttribute('checked', 'checked');
            parentLi.classList.remove('horizontal-filter__item--indeterminate');
            this.updateParentIcon(parentLi, 'checked');
            this.addToSelected(parentCheckbox.value);
        } else if (selectedChildren.length > 0) {
            // Some children selected - set parent to indeterminate
            parentCheckbox.checked = false;
            parentCheckbox.indeterminate = true;
            parentCheckbox.removeAttribute('checked');
            parentLi.classList.add('horizontal-filter__item--indeterminate');
            this.updateParentIcon(parentLi, 'indeterminate');
            this.removeFromSelected(parentCheckbox.value);
        }
    }

    private updateParentIcon(parentLi: Element, state: 'normal' | 'checked' | 'indeterminate') {
        const checkIcon = parentLi.querySelector('.icon--check') as HTMLElement;
        const minusIcon = parentLi.querySelector('.icon--minus') as HTMLElement;
        
        if (!checkIcon || !minusIcon) return;

        switch (state) {
            case 'normal':
            case 'checked':
                checkIcon.style.display = '';
                minusIcon.style.display = 'none';
                break;
            case 'indeterminate':
                checkIcon.style.display = 'none';
                minusIcon.style.display = '';
                break;
        }
    }

    private updateDisplayLabel() {
        const dropdownBtn = this.element.querySelector('.horizontal-filter__dropdown-btn span') as HTMLElement;
        if (!dropdownBtn) return;

        if (this.selectedFilters.length === 0) {
            dropdownBtn.textContent = this.options.filterLabel;
        } else {
            // Get the labels of selected items
            const selectedLabels = this.getSelectedLabels();
            if (selectedLabels.length <= 3) {
                // Show individual labels separated by commas
                dropdownBtn.textContent = selectedLabels.join(', ');
            } else {
                // Show first 3 labels and add ... for more
                const firstThree = selectedLabels.slice(0, 3);
                dropdownBtn.textContent = firstThree.join(', ') + '...';
            }
        }
    }

    private updateClearButtonVisibility() {
        const clearBtn = this.element.querySelector('.horizontal-filter__clear-btn') as HTMLElement;
        if (!clearBtn) return;

        if (this.selectedFilters.length > 0) {
            // Show the clear button - let CSS handle positioning via "open" class
            // No inline styles needed, CSS will handle it
            clearBtn.style.display = '';
            this.element.classList.add('horizontal-filter--active');
        } else {
            clearBtn.style.display = 'none';
            this.element.classList.remove('horizontal-filter--active');
        }
    }

    private getSelectedLabels(): string[] {
        const labels: string[] = [];
        
        this.selectedFilters.forEach(filterValue => {
            // Find the label for this filter value in the data
            const item = this.findItemByKey(this.options.data, filterValue);
            if (item) {
                labels.push(item.label);
            }
        });
        
        return labels;
    }

    private findItemByKey(items: any[], key: string): any {
        for (const item of items) {
            if (item.key === key) {
                return item;
            }
            
            // Check children recursively
            if (item.children && item.children.length > 0) {
                const childItem = this.findItemByKey(item.children, key);
                if (childItem) {
                    return childItem;
                }
            }
        }
        
        return null;
    }

    // Public method to update data without losing selection
    public updateData(newData: any[]) {
        this.options.data = newData;
        this.render(); // This will now preserve the selection state
        this.restoreIndeterminateStates(); // Restore indeterminate states after render
    }

    private restoreIndeterminateStates() {
        // Find all parent items and check their children state
        const parentItems = this.element.querySelectorAll('.horizontal-filter__item--has-children');
        
        parentItems.forEach(parentLi => {
            const parentCheckbox = parentLi.querySelector('.pwc-studio-checkbox') as HTMLInputElement;
            if (!parentCheckbox) return;

            // Check children state
            const siblingCheckboxes = parentLi.querySelectorAll('.horizontal-filter__nested-items .pwc-studio-checkbox') as NodeListOf<HTMLInputElement>;
            const selectedChildren = Array.from(siblingCheckboxes).filter(cb => cb.checked);
            const totalChildren = siblingCheckboxes.length;

            if (selectedChildren.length === 0) {
                // No children selected - normal state
                parentCheckbox.checked = false;
                parentCheckbox.indeterminate = false;
                parentLi.classList.remove('horizontal-filter__item--indeterminate');
                this.updateParentIcon(parentLi, 'normal');
            } else if (selectedChildren.length === totalChildren) {
                // All children selected - checked state
                parentCheckbox.checked = true;
                parentCheckbox.indeterminate = false;
                parentLi.classList.remove('horizontal-filter__item--indeterminate');
                this.updateParentIcon(parentLi, 'checked');
            } else {
                // Some children selected - indeterminate state
                parentCheckbox.checked = false;
                parentCheckbox.indeterminate = true;
                parentLi.classList.add('horizontal-filter__item--indeterminate');
                this.updateParentIcon(parentLi, 'indeterminate');
            }
        });
    }

    // Public method to set selected filters (for restoring state)
    public setSelectedFilters(selectedKeys: string[]) {
        this.selectedFilters = selectedKeys ? [...selectedKeys] : [];
        
        // Update checkboxes to reflect the selection
        const checkboxes = this.element.querySelectorAll('.pwc-studio-checkbox') as NodeListOf<HTMLInputElement>;
        checkboxes.forEach(checkbox => {
            if (!checkbox.value) return;
            const isSelected = this.selectedFilters.includes(checkbox.value);
            checkbox.checked = isSelected;
            if (isSelected) {
                checkbox.setAttribute('checked', 'checked');
            } else {
                checkbox.removeAttribute('checked');
                checkbox.indeterminate = false;
            }
        });
        
        // Update display and button state
        this.updateDisplayLabel();
        this.updateClearButtonVisibility();
        this.restoreIndeterminateStates();
    }

    // Public getter for element
    public get htmlElement(): HTMLElement {
        return this.element;
    }
}